import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { IAuthService } from '@/services/auth/auth.service.interface'
import type { User, LoginOptions, LogoutOptions, AuthResult } from '@/types/auth.types'
import {
  clearRememberMePreference,
  getRememberMePreference,
  shouldExpireSession,
  setLastOrgId,
} from '@/utils/secure-storage'
import { extractOrgId } from '@/utils/token.utils'
import { isInactivityExpired, updateLastActivity, clearLastActivity } from '@/utils/session.utils'
import { INACTIVITY_TIMEOUT_MS } from '@/utils/session.config'
import { reportError } from '@/lib/error-reporting'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const user = ref<User | null>(null)
  const error = ref<Error | null>(null)

  // Auth service instance (injected for testability)
  let authService: IAuthService | null = null

  // Set auth service (for dependency injection)
  function setAuthService(service: IAuthService) {
    authService = service
  }

  // Getters
  const currentUser = computed(() => user.value)

  function hasPermission(permission: string): boolean {
    return user.value?.permissions?.includes(permission) ?? false
  }

  function hasRole(role: string): boolean {
    const roles = user.value?.roles ?? []
    // #561: the 'admin' app-role was renamed to 'organisation-admin'. Treat the two as
    // equivalent so role checks work across the data migration (either value may be
    // stored) and regardless of which literal a caller passes. Drop after the legacy
    // 'admin' rows are fully migrated.
    if (role === 'admin' || role === 'organisation-admin') {
      return roles.includes('admin') || roles.includes('organisation-admin')
    }
    return roles.includes(role)
  }

  // Actions
  async function initialize(): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.initialize()
      if (!result.ok) {
        error.value = result.error
        return result
      }

      // Check if user is already authenticated
      await checkAuth()

      // After checking auth, apply the correct session expiry logic.
      if (isAuthenticated.value) {
        const rememberMe = await getRememberMePreference()

        if (rememberMe) {
          // Remember Me is ON: honour the 7-day hard limit but skip the
          // inactivity check — the user explicitly asked to stay logged in.
          // Also reset last-activity so the inactivity timer starts fresh
          // from this browser open.
          const hardExpired = await shouldExpireSession()
          if (hardExpired) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[AuthStore] Remember Me session exceeded 7-day limit. Logging out...')
            }
            await logout({ returnTo: window.location.origin })
            return {
              ok: false,
              error: new Error('Your saved session has expired. Please log in again.'),
            }
          }
          // Reset inactivity timer so the 6-hour window starts from now
          updateLastActivity()
        } else {
          // Remember Me is OFF: enforce the inactivity window (6 hours).
          if (isInactivityExpired(INACTIVITY_TIMEOUT_MS)) {
            if (import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.log('[AuthStore] Session expired due to inactivity. Logging out...')
            }
            await logout({ returnTo: window.location.origin })
            return {
              ok: false,
              error: new Error('Session expired due to inactivity. Please log in again.'),
            }
          }
        }
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Initialization failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function login(options?: LoginOptions): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.login(options)
      if (!result.ok) {
        error.value = result.error
        return result
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Login failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function logout(options?: LogoutOptions): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.logout(options)

      // Clear state regardless of logout result
      isAuthenticated.value = false
      user.value = null

      // Clear remember me preference and last activity on logout
      clearRememberMePreference()
      clearLastActivity()

      if (!result.ok) {
        error.value = result.error
        return result
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Logout failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  async function handleCallback(): Promise<AuthResult<{ appState?: unknown }>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      isLoading.value = true
      error.value = null

      const result = await authService.handleRedirectCallback()
      if (!result.ok) {
        error.value = result.error
        return result
      }

      // Update auth state after successful callback
      await checkAuth()

      // #860: persist the user's Auth0 organization from the freshly-issued access token
      // so LoginPage can re-send it on the next sign-in (SPA re-login org-scoping — a
      // re-login with no `organization` otherwise falls to the tenant default connection
      // where external/invite-only users don't exist → bogus "incorrect password").
      // Best-effort: never let persistence break the callback. NOT cleared on logout
      // (surviving logout is the whole point — see setLastOrgId).
      if (isAuthenticated.value) {
        try {
          const tokenResult = await authService.getAccessToken()
          if (tokenResult.ok && tokenResult.value) {
            const orgId = extractOrgId(tokenResult.value)
            if (orgId) {
              await setLastOrgId(orgId)
            }
          }
        } catch (persistErr) {
          reportError(persistErr, { where: 'auth.store.handleCallback.persistOrg' })
        }
      }

      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Callback handling failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Load user roles from database and merge with Auth0 roles
   * @param _userId - User ID to load roles for (reserved for future use)
   */
  async function loadDatabaseRoles(_userId: string): Promise<void> {
    try {
      // Get access token for API authentication
      const tokenResult = await authService?.getAccessToken()
      if (!tokenResult?.ok || !tokenResult.value) {
        if (import.meta.env.DEV) {
          console.warn('[AuthStore] Failed to get access token for role loading')
        }
        // Fail closed (#561): the DB is the single source of truth for authorization, so on
        // ANY failure clear roles to [] — never leave stale Auth0-claim roles in place (they
        // would let the UI/route guards grant access the BFF rejects with a 403).
        if (user.value) {
          user.value.roles = []
        }
        return
      }

      // Call BFF API to get user roles (direct URL to BFF, not through proxy)
      const bffUrl = import.meta.env.VITE_BFF_URL || 'http://localhost:3001/api'
      const response = await fetch(`${bffUrl}/user-roles/me`, {
        headers: {
          Authorization: `Bearer ${tokenResult.value}`,
        },
      })

      if (!response.ok) {
        if (import.meta.env.DEV) {
          console.warn('[AuthStore] Failed to load database roles:', response.status)
        }
        // Fail closed (#561): the DB is the single source of truth for authorization, so on
        // ANY failure clear roles to [] — never leave stale Auth0-claim roles in place (they
        // would let the UI/route guards grant access the BFF rejects with a 403).
        if (user.value) {
          user.value.roles = []
        }
        return
      }

      const data = await response.json()
      const dbRoles: string[] = data.roles?.map((r: { role_name: string }) => r.role_name) || []

      // #561: the DATABASE (user_roles) is the single source of truth for authorization —
      // the BFF enforces ONLY DB roles. We must NOT merge Auth0 token role claims, or the
      // UI would show admin features (and pass route guards) for roles the backend rejects
      // (causing 403s), and a stale/independent Auth0 claim could imply access a user does
      // not actually have. Set the role set to exactly the DB roles so frontend gating
      // matches backend enforcement.
      if (user.value) {
        user.value.roles = dbRoles
      }

      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[AuthStore] Roles loaded (DB authoritative):', dbRoles)
      }
    } catch (err) {
      reportError(err, { where: 'auth.store.loadDatabaseRoles' })
      // Ensure roles array exists even on failure
      if (user.value && !user.value.roles) {
        user.value.roles = []
      }
      // Don't throw - role loading failure shouldn't break authentication
    }
  }

  async function checkAuth(): Promise<AuthResult<void>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      const authResult = await authService.isAuthenticated()
      if (!authResult.ok) {
        error.value = authResult.error
        return authResult
      }

      isAuthenticated.value = authResult.value

      if (authResult.value) {
        // Get user profile
        const userResult = await authService.getUser()
        if (!userResult.ok) {
          error.value = userResult.error
          return userResult
        }
        user.value = userResult.value

        // Load database roles and merge with Auth0 roles
        if (user.value?.sub) {
          await loadDatabaseRoles(user.value.sub)
        }
      } else {
        user.value = null
      }

      return { ok: true, value: undefined }
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Auth check failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    }
  }

  async function refreshToken(): Promise<AuthResult<string>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      error.value = null

      const result = await authService.getAccessToken()
      if (result.ok) {
        return result
      }

      // Token fetch failed. Before propagating the error (which causes services to call
      // emitSessionExpired()), attempt an SSO session renewal via checkSession().
      // auth0Client.checkSession() calls the /authorize endpoint with prompt=none in a
      // hidden iframe, using the Auth0 first-party SSO cookie. If the user's Auth0 session
      // is still alive, this issues a new access token silently. This path is the only
      // renewal mechanism available when refresh tokens are not issued (e.g. when the
      // Auth0 API audience does not have "Allow Offline Access" enabled).
      //
      // IMPORTANT: At this point the Auth0 localStorage cache is already empty (Auth0
      // leeway removed it ~60 s before natural expiry), so checkSession() cannot cause
      // premature cache clearing — there is nothing left to clear.
      //
      // Note: This will FAIL silently in browsers that block third-party cookies (Chrome
      // 80+ with enhanced protection, Safari ITP) because the hidden iframe cannot access
      // the first-party auth0 cookie. In those environments the root fix is either to:
      // - Enable refresh tokens (Auth0 API → Allow Offline Access), or
      // - Increase the access token lifetime in the Auth0 dashboard (APIs → Token Expiration).
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log(
          '[AuthStore] Access token expired — attempting SSO session renewal via checkSession()'
        )
      }

      const sessionResult = await authService.checkSession()
      if (sessionResult.ok) {
        // SSO renewal succeeded — retry token acquisition
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log(
            '[AuthStore] SSO renewal succeeded — session extended without user interaction'
          )
        }
        const retryResult = await authService.getAccessToken()
        if (retryResult.ok) {
          return retryResult
        }
        // Retry failed even after successful checkSession — fall through to return original error
      } else if (import.meta.env.DEV) {
        console.warn(
          '[AuthStore] SSO renewal failed (third-party cookies blocked or session expired) — session cannot be extended'
        )
      }

      error.value = result.error
      return result
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Token refresh failed')
      error.value = errorObj
      return { ok: false, error: errorObj }
    }
  }

  /**
   * Get auth service instance (for storage initialization)
   */
  function getAuthService(): IAuthService | null {
    return authService
  }

  /**
   * Get access token for API calls
   */
  async function getAccessToken(): Promise<AuthResult<string>> {
    if (!authService) {
      return {
        ok: false,
        error: new Error('Auth service not initialized'),
      }
    }

    try {
      return await authService.getAccessToken()
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Failed to get access token')
      return { ok: false, error: errorObj }
    }
  }

  return {
    // State
    isAuthenticated,
    isLoading,
    user,
    error,
    // Getters
    currentUser,
    hasPermission,
    hasRole,
    // Actions
    setAuthService,
    getAuthService,
    initialize,
    login,
    logout,
    handleCallback,
    checkAuth,
    refreshToken,
    getAccessToken,
  }
})
